import { ProductStatusBadge } from "@/components";
import { Alert, App as AntApp, Button, Collapse, Empty, Input, Modal, Skeleton } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import {
  ArrowLeftIcon,
  CopyPlusIcon,
  Edit3Icon,
  EyeIcon,
  MapPinIcon,
  PlusIcon,
  SendIcon,
  Trash2Icon,
  UsersRoundIcon,
} from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router";
import {
  PortalItineraryListItem,
  PortalTripRequestStatus,
  useDiscardPortalItineraryMutation,
  useDiscardPortalTripRequestMutation,
  useDuplicatePortalItineraryMutation,
  usePortalTripRequestQuery,
  useSubmitPortalItineraryMutation,
  useUpdatePortalTripRequestMutation,
} from "../actions/itinerary.actions";
import { PortalPageHeader, PortalPageShell } from "../components/PortalProduct";
import type { PortalWorkspaceContext } from "../components/PortalWorkspaceLayout";
import { useEditQuote } from "../hooks/useEditQuote";
import { getPortalPaths } from "../lib/routes";

const getError = (error: unknown, fallback: string) =>
  axios.isAxiosError<{ error?: string }>(error) ? error.response?.data?.error || fallback : fallback;
const formatDate = (value: string | null) => (value ? dayjs(value).format("DD MMM YYYY") : "Not set");
const formatMoney = (amount: number, currency: string) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 }).format(amount);
const statusTone = (status: PortalTripRequestStatus | string): "gold" | "neutral" | "success" | "danger" => {
  if (status === "confirmed") return "success";
  if (status === "cancelled") return "danger";
  if (status === "returned" || status === "submitted") return "gold";
  return "neutral";
};

type OptionCardProps = {
  option: PortalItineraryListItem;
  canDiscard: boolean;
  onEdit: (id: string) => void;
  onDuplicate: (id: string) => void;
  onSubmit: (option: PortalItineraryListItem) => void;
  onDiscard: (option: PortalItineraryListItem) => void;
};

const OptionCard = ({ option, canDiscard, onEdit, onDuplicate, onSubmit, onDiscard }: OptionCardProps) => {
  const paths = getPortalPaths();
  return (
    <article
      className={`rounded-xl border bg-white p-4 shadow-[0_1px_2px_rgba(19,27,42,0.04)] ${option.isSubmittedOption ? "border-(--brand-primary) ring-1 ring-(--brand-primary)/30" : "border-gray-200"}`}
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-[#131B2A]">Quote {option.optionNumber}</h3>
            {option.requestStatus.key === "confirmed" || option.requestStatus.key === "cancelled" ? (
              <ProductStatusBadge tone={statusTone(option.requestStatus.key)}>
                {option.requestStatus.label}
              </ProductStatusBadge>
            ) : null}
            {option.isSubmittedOption ? <ProductStatusBadge tone="gold">Confirmed quote</ProductStatusBadge> : null}
          </div>
          <p className="mt-3 flex items-center gap-1.5 text-sm font-medium text-[#131B2A]">
            <MapPinIcon size={15} className="text-gray-400" />
            {option.destinations.join(" · ") || "No destination"}
          </p>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-500">
            <span>
              {formatDate(option.startDate)} · {option.nights} nights
            </span>
            <span className="flex items-center gap-1">
              <UsersRoundIcon size={13} />
              {option.travellers.summary}
            </span>
            <span className="font-semibold text-[#131B2A]">
              {formatMoney(option.totalPrice.amount, option.totalPrice.currency)}
            </span>
            <span>Updated {formatDate(option.updatedAt)}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to={paths.itinerary(option.id)}>
            <Button icon={<EyeIcon size={14} />}>View</Button>
          </Link>
          {option.canEdit ? (
            <Button icon={<Edit3Icon size={14} />} onClick={() => onEdit(option.id)}>
              Edit
            </Button>
          ) : null}
          {option.canEdit ? (
            <Button icon={<CopyPlusIcon size={14} />} onClick={() => onDuplicate(option.id)}>
              Duplicate
            </Button>
          ) : null}
          {option.canSubmit ? (
            <Button type="primary" icon={<SendIcon size={14} />} onClick={() => onSubmit(option)}>
              Confirm Quote
            </Button>
          ) : null}
          {canDiscard && option.canEdit ? (
            <Button danger icon={<Trash2Icon size={14} />} onClick={() => onDiscard(option)}>
              Discard
            </Button>
          ) : null}
        </div>
      </div>
    </article>
  );
};

/** Displays one trip request and all itinerary alternatives owned by the agency. */
const PortalTripRequestDetailPage = () => {
  const { leadId = "" } = useParams();
  const { organisationName } = useOutletContext<PortalWorkspaceContext>();
  const paths = getPortalPaths();
  const navigate = useNavigate();
  const { message, modal } = AntApp.useApp();
  const query = usePortalTripRequestQuery(leadId);
  const updateMutation = useUpdatePortalTripRequestMutation();
  const discardRequestMutation = useDiscardPortalTripRequestMutation();
  const discardOptionMutation = useDiscardPortalItineraryMutation();
  const duplicateMutation = useDuplicatePortalItineraryMutation();
  const submitMutation = useSubmitPortalItineraryMutation();
  const { startEdit, modalHolder: editQuoteModal } = useEditQuote();
  const [guestModalOpen, setGuestModalOpen] = useState(false);
  const [guestName, setGuestName] = useState("");

  if (query.isLoading)
    return (
      <PortalPageShell>
        <Skeleton active paragraph={{ rows: 8 }} />
      </PortalPageShell>
    );
  if (query.isError || !query.data)
    return (
      <PortalPageShell>
        <Alert
          type="error"
          showIcon
          message="Unable to load this trip"
          action={<Button onClick={() => void query.refetch()}>Retry</Button>}
        />
      </PortalPageShell>
    );

  const request = query.data;
  const activeOptions = request.options.filter((option) => option.requestStatus.key !== "cancelled");
  const cancelledOptions = request.options.filter((option) => option.requestStatus.key === "cancelled");

  const editGuest = () => {
    setGuestName(request.guestName);
    setGuestModalOpen(true);
  };
  const saveGuest = async () => {
    try {
      await updateMutation.mutateAsync({ leadId: request.id, guestName });
      message.success("Guest name updated.");
      setGuestModalOpen(false);
    } catch (error) {
      message.error(getError(error, "Unable to update the guest name"));
    }
  };
  const duplicate = async (itineraryId: string) => {
    try {
      const result = await duplicateMutation.mutateAsync(itineraryId);
      message.success("Alternative created.");
      navigate(paths.editItinerary(result.id));
    } catch (error) {
      message.error(getError(error, "Unable to duplicate this option"));
    }
  };
  const submit = (option: PortalItineraryListItem) =>
    modal.confirm({
      title: `Confirm Quote ${option.optionNumber}?`,
      content: `This sends the quote to ${organisationName}. You will not be able to edit any quote in this trip unless ${organisationName} returns it.`,
      okText: "Confirm Quote",
      cancelText: "Keep editing",
      onOk: async () => {
        try {
          await submitMutation.mutateAsync(option.id);
          message.success(`Quote confirmed with ${organisationName}.`);
          await query.refetch();
        } catch (error) {
          message.error(getError(error, "Unable to confirm this quote"));
        }
      },
    });
  const discardOption = (option: PortalItineraryListItem) =>
    modal.confirm({
      title: `Discard Option ${option.optionNumber}?`,
      content: "The quote will move to cancelled history and cannot be restored or confirmed.",
      okText: "Discard option",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await discardOptionMutation.mutateAsync({ itineraryId: option.id, leadId: request.id });
          message.success("Itinerary option discarded.");
        } catch (error) {
          message.error(getError(error, "Unable to discard this option"));
        }
      },
    });
  const discardRequest = () =>
    modal.confirm({
      title: "Discard this trip?",
      content: "The trip and all active quotes will be cancelled and become read-only. Audit history will be retained.",
      okText: "Discard trip",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await discardRequestMutation.mutateAsync(request.id);
          message.success("Trip discarded.");
          await query.refetch();
        } catch (error) {
          message.error(getError(error, "Unable to discard this trip"));
        }
      },
    });

  return (
    <PortalPageShell>
      <div className="space-y-4">
        <Link
          to={paths.itineraries}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500 no-underline hover:text-(--brand-primary)"
        >
          <ArrowLeftIcon size={14} />
          Quotes
        </Link>
        <PortalPageHeader
          title={request.guestName || "Unnamed guest"}
          meta={`Trip ${request.reference}`}
          description={`${request.optionCount} active quote${request.optionCount === 1 ? "" : "s"}`}
          actions={
            request.canEdit ? (
              <>
                <Button icon={<Edit3Icon size={14} />} onClick={editGuest}>
                  Edit guest
                </Button>
                <Link to={paths.newRequestOption(request.id)}>
                  <Button type="primary" icon={<PlusIcon size={14} />}>
                    Create blank quote
                  </Button>
                </Link>
                <Button danger icon={<Trash2Icon size={14} />} onClick={discardRequest}>
                  Discard trip
                </Button>
              </>
            ) : undefined
          }
        />

        {request.requestStatus.key === "returned" ? (
          <div>
            <Alert
              type="warning"
              showIcon
              message={`Returned by ${organisationName} - action required`}
              description={request.portalReturnReason || "Review the quotes and confirm one again."}
            />
          </div>
        ) : null}

        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-[0_1px_2px_rgba(19,27,42,0.04)]">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-400">Trip status</p>
              <div className="mt-2">
                <ProductStatusBadge tone={statusTone(request.requestStatus.key)}>
                  {request.requestStatus.label}
                </ProductStatusBadge>
              </div>
            </div>
            <div className="text-xs text-gray-500">
              <p>Created {formatDate(request.createdAt)}</p>
              <p className="mt-1">Updated {formatDate(request.updatedAt)}</p>
            </div>
          </div>
          {request.summary ? (
            <div className="mt-4 grid gap-3 border-t border-gray-100 pt-4 sm:grid-cols-3">
              <div>
                <p className="text-xs text-gray-400">Route</p>
                <p className="mt-1 text-sm font-semibold text-[#131B2A]">{request.summary.destinations.join(" · ")}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Travel</p>
                <p className="mt-1 text-sm font-semibold text-[#131B2A]">
                  {formatDate(request.summary.startDate)} · {request.summary.nights} nights
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Travellers</p>
                <p className="mt-1 text-sm font-semibold text-[#131B2A]">{request.summary.travellers.summary}</p>
              </div>
            </div>
          ) : null}
        </section>

        <section aria-labelledby="active-options-heading" className="space-y-3">
          <div>
            <h2 id="active-options-heading" className="text-base font-semibold text-[#131B2A]">
              Quotes
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">
              Compare alternatives, save as often as needed, then confirm one quote.
            </p>
          </div>
          {activeOptions.length ? (
            activeOptions.map((option) => (
              <OptionCard
                key={option.id}
                option={option}
                canDiscard={activeOptions.length > 1 && request.canEdit}
                onEdit={startEdit}
                onDuplicate={duplicate}
                onSubmit={submit}
                onDiscard={discardOption}
              />
            ))
          ) : (
            <Empty description="No active quotes" />
          )}
        </section>

        {cancelledOptions.length ? (
          <Collapse
            items={[
              {
                key: "cancelled",
                label: `Cancelled quotes (${cancelledOptions.length})`,
                children: (
                  <div className="space-y-3">
                    {cancelledOptions.map((option) => (
                      <OptionCard
                        key={option.id}
                        option={option}
                        canDiscard={false}
                        onEdit={startEdit}
                onDuplicate={duplicate}
                        onSubmit={submit}
                        onDiscard={discardOption}
                      />
                    ))}
                  </div>
                ),
              },
            ]}
          />
        ) : null}
      </div>

      <Modal
        title="Guest name"
        open={guestModalOpen}
        okText="Save guest"
        confirmLoading={updateMutation.isPending}
        onOk={saveGuest}
        onCancel={() => setGuestModalOpen(false)}
      >
        <label htmlFor="portal-guest-name" className="mb-1.5 block text-xs font-semibold text-gray-700">
          Guest name (optional)
        </label>
        <Input
          id="portal-guest-name"
          value={guestName}
          maxLength={128}
          placeholder="For example, Sarah Sharma"
          onChange={(event) => setGuestName(event.target.value)}
          onPressEnter={() => void saveGuest()}
        />
        <p className="mt-2 text-xs text-gray-500">
          This name identifies the request to your agency and {organisationName}.
        </p>
      </Modal>
      {editQuoteModal}
    </PortalPageShell>
  );
};

export default PortalTripRequestDetailPage;
